@if (loading) {
  <div id="sm-loader" class="row" role="status" aria-live="polite">
    <div class="col">
      <div id="loader" aria-hidden="true">
        <div class="ls-particles ls-part-1"></div>
        <div class="ls-particles ls-part-2"></div>
        <div class="ls-particles ls-part-3"></div>
        <div class="ls-particles ls-part-4"></div>
        <div class="ls-particles ls-part-5"></div>
        <div class="lightsaber ls-left ls-green"></div>
        <div class="lightsaber ls-right ls-red"></div>
      </div>
    </div>
  </div>
}

@if (!loading) {
  <div class="container-fluid dashboard-shell">
    @if (showTitle) {
      <div class="toolbar-row mb-4">
        <div class="toolbar-row__search">
          <p-text-field-wrapper [hideLabel]="true" label="Search resources">
            <input
              type="search"
              name="resource-search"
              placeholder="Search PFS Services, Lambdas and Frontends"
              [value]="searchTerm"
              (input)="onSearchInput($event)"
              aria-label="Search resources"
            />
          </p-text-field-wrapper>
        </div>
        <div class="toolbar-row__actions">
          <div class="settings-anchor">
            <button
              type="button"
              class="settings-btn"
              [class.settings-btn--active]="settingsOpen"
              (click)="toggleSettings()"
              [attr.aria-expanded]="settingsOpen"
              aria-controls="settings-panel"
              aria-label="Settings"
              title="Manage visible resources"
            >
              <mat-icon>settings</mat-icon>
              @if (hiddenCount > 0) {
                <span class="settings-btn__badge">{{ hiddenCount }}</span>
              }
            </button>

            @if (settingsOpen) {
              <div class="settings-backdrop" (click)="toggleSettings()" aria-hidden="true"></div>
              <div id="settings-panel" class="settings-panel" role="dialog" aria-label="Visibility settings">
                <div class="settings-panel__header">
                  <span class="settings-panel__title">Visible resources</span>
                  @if (hiddenCount > 0) {
                    <button type="button" class="settings-panel__reset-btn" (click)="hiddenItems.resetAll()">
                      Reset all
                    </button>
                  }
                </div>
                <div class="settings-panel__toggles">
                  <label class="settings-panel__item settings-panel__item--compact">
                    <mat-slide-toggle
                      class="settings-panel__stack-toggle"
                      [checked]="showStackName"
                      (change)="toggleShowStackName($event.checked)"
                    ></mat-slide-toggle>
                    <span class="settings-panel__item-name">Show stack name at bottom of cards</span>
                  </label>
                </div>
                <div class="settings-panel__columns">
                  <div class="settings-panel__col">
                    <span class="settings-panel__col-heading">
                      PFS Services
                      @if (hiddenItems.hiddenServicesCount > 0) {
                        <span class="settings-panel__hidden-count">({{ hiddenItems.hiddenServicesCount }} hidden)</span>
                      }
                    </span>
                    <div class="settings-panel__list">
                      @for (service of allServices; track service.name) {
                        <label class="settings-panel__item">
                          <mat-checkbox
                            class="settings-panel__checkbox"
                            [checked]="!hiddenItems.isServiceHidden(service.name)"
                            (change)="hiddenItems.toggleService(service.name)"
                            color="primary"
                          ></mat-checkbox>
                          <span class="settings-panel__item-name">{{ service.name }}</span>
                        </label>
                      }
                      @if (allServices.length === 0) {
                        <p-text class="settings-panel__empty">No services available.</p-text>
                      }
                    </div>
                  </div>
                  <div class="settings-panel__col">
                    <span class="settings-panel__col-heading">
                      Lambdas
                      @if (hiddenItems.hiddenLambdasCount > 0) {
                        <span class="settings-panel__hidden-count">({{ hiddenItems.hiddenLambdasCount }} hidden)</span>
                      }
                    </span>
                    <div class="settings-panel__list">
                      @for (lambda of allLambdas; track lambda.name) {
                        <label class="settings-panel__item">
                          <mat-checkbox
                            class="settings-panel__checkbox"
                            [checked]="!hiddenItems.isLambdaHidden(lambda.name ?? '')"
                            (change)="hiddenItems.toggleLambda(lambda.name ?? '')"
                            color="primary"
                          ></mat-checkbox>
                          <span class="settings-panel__item-name">{{ lambda.name }}</span>
                        </label>
                      }
                      @if (allLambdas.length === 0) {
                        <p-text class="settings-panel__empty">No lambdas available.</p-text>
                      }
                    </div>
                  </div>
                  <div class="settings-panel__col">
                    <span class="settings-panel__col-heading">
                      Frontends
                      @if (hiddenItems.hiddenFrontendsCount > 0) {
                        <span class="settings-panel__hidden-count"
                          >({{ hiddenItems.hiddenFrontendsCount }} hidden)</span
                        >
                      }
                    </span>
                    <div class="settings-panel__list">
                      @for (frontend of allFrontends; track frontend.name) {
                        <label class="settings-panel__item">
                          <mat-checkbox
                            class="settings-panel__checkbox"
                            [checked]="!hiddenItems.isFrontendHidden(frontend.name)"
                            (change)="hiddenItems.toggleFrontend(frontend.name)"
                            color="primary"
                          ></mat-checkbox>
                          <span class="settings-panel__item-name">{{ frontend.name }}</span>
                        </label>
                      }
                      @if (allFrontends.length === 0) {
                        <p-text class="settings-panel__empty">No frontends available.</p-text>
                      }
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }

    <section class="resource-section">
      <button
        type="button"
        class="resource-section__header"
        [attr.aria-expanded]="servicesOpen"
        aria-controls="section-services"
        (click)="toggleSection('services')"
      >
        <p-icon
          class="resource-section__chevron"
          [name]="'arrow-head-right'"
          [class.resource-section__chevron--open]="servicesOpen"
          aria-hidden="true"
        ></p-icon>
        <p-heading [size]="'medium'" [tag]="'h2'"> PFS Services ({{ filteredServices.length }}) </p-heading>
      </button>

      <div class="resource-section__collapse" [class.resource-section__collapse--open]="servicesOpen">
        <div id="section-services" class="resource-section__body" [attr.inert]="servicesOpen ? null : ''">
          @if (filteredServices.length === 0) {
            <p-text class="empty-state">No PFS Services match the current search.</p-text>
          }
          <div class="row g-3">
            @for (service of filteredServices; track service.name) {
              <div class="col-12 col-md-6 col-xl-4 mb-3">
                <mat-card class="resource-card service-card" [appearance]="'outlined'">
                  <mat-card-content>
                    <div class="service-card__header">
                      <div class="service-card__title">
                        <div class="resource-title-row">
                          <p-heading class="heading" [size]="'medium'" [tag]="'h3'">
                            {{ service.name }}
                          </p-heading>
                          @if (service.description.trim().length > 0) {
                            <button
                              type="button"
                              class="description-tooltip-trigger"
                              [matTooltip]="service.description"
                              matTooltipPosition="above"
                              aria-label="Show service description"
                            >
                              <mat-icon fontIcon="info"></mat-icon>
                            </button>
                          }
                        </div>
                      </div>
                      <div class="service-card__status">
                        <app-service-status [service]="service"></app-service-status>
                      </div>
                    </div>
                    <div class="row g-3 service-meta">
                      <div class="col-12 col-md-4">
                        <div class="service-meta__label">Dev</div>
                        <div class="service-meta__value">
                          {{ service.devVersion }}
                        </div>
                        @if (service.devApiDocumentationUrl && service.devApiDocumentationUrl !== "") {
                          <a
                            class="service-meta__link"
                            href="{{ service.devApiDocumentationUrl }}"
                            target="_blank"
                            rel="noopener noreferrer"
                            >OpenAPI</a
                          >
                        }
                      </div>
                      <div class="col-12 col-md-4">
                        <div class="service-meta__label">Preview</div>
                        <div class="service-meta__value">
                          {{ service.previewVersion }}
                        </div>
                        @if (service.previewApiDocumentationUrl && service.previewApiDocumentationUrl !== "") {
                          <a
                            class="service-meta__link"
                            href="{{ service.previewApiDocumentationUrl }}"
                            target="_blank"
                            rel="noopener noreferrer"
                            >OpenAPI</a
                          >
                        }
                      </div>
                      <div class="col-12 col-md-4">
                        <div class="service-meta__label">Prod</div>
                        <div class="service-meta__value">
                          {{ service.prodVersion }}
                        </div>
                        @if (service.prodApiDocumentationUrl && service.prodApiDocumentationUrl !== "") {
                          <a
                            class="service-meta__link"
                            href="{{ service.prodApiDocumentationUrl }}"
                            target="_blank"
                            rel="noopener noreferrer"
                            >OpenAPI</a
                          >
                        }
                      </div>
                    </div>
                    @if ((service.stackName ?? "").trim().length > 0) {
                      <div
                        class="resource-card__stack-shell"
                        [class.resource-card__stack-shell--visible]="showStackName"
                      >
                        <div class="resource-card__stack">Stack: {{ service.stackName }}</div>
                      </div>
                    }
                  </mat-card-content>
                </mat-card>
              </div>
            }
          </div>
        </div>
      </div>
    </section>

    <section class="resource-section">
      <button
        type="button"
        class="resource-section__header"
        [attr.aria-expanded]="lambdasOpen"
        aria-controls="section-lambdas"
        (click)="toggleSection('lambdas')"
      >
        <p-icon
          class="resource-section__chevron"
          [name]="'arrow-head-right'"
          [class.resource-section__chevron--open]="lambdasOpen"
          aria-hidden="true"
        ></p-icon>
        <p-heading [size]="'medium'" [tag]="'h2'"> Lambdas ({{ filteredLambdas.length }}) </p-heading>
      </button>

      <div class="resource-section__collapse" [class.resource-section__collapse--open]="lambdasOpen">
        <div id="section-lambdas" class="resource-section__body" [attr.inert]="lambdasOpen ? null : ''">
          @if (filteredLambdas.length === 0) {
            <p-text class="empty-state">No Lambdas match the current search.</p-text>
          }
          <div class="row g-3">
            @for (lambda of filteredLambdas; track lambda.name) {
              <div class="col-12 col-md-6 col-xl-4 mb-3">
                <mat-card class="resource-card lambda-card" [appearance]="'outlined'">
                  <mat-card-content>
                    <div class="lambda-card__title">
                      <div class="resource-title-row">
                        <p-heading class="heading" [size]="'medium'" [tag]="'h3'">
                          {{ lambda.name }}
                        </p-heading>
                        @if (lambda.description.trim().length > 0) {
                          <button
                            type="button"
                            class="description-tooltip-trigger"
                            [matTooltip]="lambda.description"
                            matTooltipPosition="above"
                            aria-label="Show lambda description"
                          >
                            <mat-icon fontIcon="info"></mat-icon>
                          </button>
                        }
                      </div>
                    </div>
                    <div class="row g-3 service-meta">
                      <div class="col-12 col-md-4">
                        <div class="service-meta__label">Dev</div>
                        <div class="service-meta__value">
                          {{ lambda.devVersion }}
                        </div>
                      </div>
                      <div class="col-12 col-md-4">
                        <div class="service-meta__label">Preview</div>
                        <div class="service-meta__value">
                          {{ lambda.previewVersion }}
                        </div>
                      </div>
                      <div class="col-12 col-md-4">
                        <div class="service-meta__label">Prod</div>
                        <div class="service-meta__value">
                          {{ lambda.prodVersion }}
                        </div>
                      </div>
                    </div>
                    @if ((lambda.stackName ?? "").trim().length > 0) {
                      <div
                        class="resource-card__stack-shell"
                        [class.resource-card__stack-shell--visible]="showStackName"
                      >
                        <div class="resource-card__stack">Stack: {{ lambda.stackName }}</div>
                      </div>
                    }
                  </mat-card-content>
                </mat-card>
              </div>
            }
          </div>
        </div>
      </div>
    </section>

    <section class="resource-section">
      <button
        type="button"
        class="resource-section__header"
        [attr.aria-expanded]="frontendsOpen"
        aria-controls="section-frontends"
        (click)="toggleSection('frontends')"
      >
        <p-icon
          class="resource-section__chevron"
          [name]="'arrow-head-right'"
          [class.resource-section__chevron--open]="frontendsOpen"
          aria-hidden="true"
        ></p-icon>
        <p-heading [size]="'medium'" [tag]="'h2'"> Frontends ({{ filteredFrontends.length }}) </p-heading>
      </button>

      <div class="resource-section__collapse" [class.resource-section__collapse--open]="frontendsOpen">
        <div id="section-frontends" class="resource-section__body" [attr.inert]="frontendsOpen ? null : ''">
          @if (filteredFrontends.length === 0) {
            <p-text class="empty-state">No Frontends match the current search.</p-text>
          }
          <div class="row g-3">
            @for (frontend of filteredFrontends; track frontend.name) {
              <div class="col-12 col-md-6 col-xl-4 mb-3">
                <mat-card class="resource-card frontend-card" [appearance]="'outlined'">
                  <mat-card-content>
                    <div class="frontend-card__title">
                      <div class="resource-title-row">
                        <p-heading class="heading" [size]="'medium'" [tag]="'h3'">
                          {{ frontend.name }}
                        </p-heading>
                        @if (frontend.description.trim().length > 0) {
                          <button
                            type="button"
                            class="description-tooltip-trigger"
                            [matTooltip]="frontend.description"
                            matTooltipPosition="above"
                            aria-label="Show frontend description"
                          >
                            <mat-icon fontIcon="info"></mat-icon>
                          </button>
                        }
                      </div>
                    </div>
                    <div class="row g-3 service-meta">
                      <div class="col-12 col-md-4">
                        <div class="service-meta__label">Dev</div>
                        <div class="service-meta__value">
                          @if (frontend.devVersion !== "") {
                            {{ frontend.devVersion }}
                          } @else {
                            {{ frontend.devLastModified | date: "dd.MM.yyyy" }}
                          }
                        </div>
                        @if (frontend.devUrl !== "") {
                          <a
                            class="service-meta__link"
                            href="{{ frontend.devUrl }}"
                            target="_blank"
                            rel="noopener noreferrer"
                            >Open</a
                          >
                        }
                      </div>
                      <div class="col-12 col-md-4">
                        <div class="service-meta__label">Preview</div>
                        <div class="service-meta__value">
                          @if (frontend.previewVersion !== "") {
                            {{ frontend.previewVersion }}
                          } @else {
                            {{ frontend.previewLastModified | date: "dd.MM.yyyy" }}
                          }
                        </div>
                        @if (frontend.previewUrl !== "") {
                          <a
                            class="service-meta__link"
                            href="{{ frontend.previewUrl }}"
                            target="_blank"
                            rel="noopener noreferrer"
                            >Open</a
                          >
                        }
                      </div>
                      <div class="col-12 col-md-4">
                        <div class="service-meta__label">Prod</div>
                        <div class="service-meta__value">
                          @if (frontend.prodVersion !== "") {
                            {{ frontend.prodVersion }}
                          } @else {
                            {{ frontend.prodLastModified | date: "dd.MM.yyyy" }}
                          }
                        </div>
                        @if (frontend.prodUrl !== "") {
                          <a
                            class="service-meta__link"
                            href="{{ frontend.prodUrl }}"
                            target="_blank"
                            rel="noopener noreferrer"
                            >Open</a
                          >
                        }
                      </div>
                    </div>
                  </mat-card-content>
                </mat-card>
              </div>
            }
          </div>
        </div>
      </div>
    </section>
  </div>
}
